/**
 * useQuickAttendance Hook
 * State management for Quick Attendance Wizard
 *
 * @version 2025.1.0
 */

import { useState, useCallback, useMemo } from 'react';
import { ReceptionAppointment, AttendanceStatus, AttendanceType } from '../../../../../types/reception';
import { TriageData, INITIAL_TRIAGE_DATA, hasTriageData } from '../../../../../types/triage';
import {
  WizardState,
  StepConfig,
  UseQuickAttendanceReturn,
  getApplicableSteps,
} from '../QuickAttendanceWizard.types';
import { receptionService } from '../../../../../services/receptionService';

interface UseQuickAttendanceOptions {
  appointment: ReceptionAppointment | null;
  checkIn: (appointmentId: number) => Promise<boolean>;
  startAttendance: (appointmentId: number) => Promise<boolean>;
  completeAttendance?: (appointmentId: number, attendanceType?: AttendanceType) => Promise<boolean>;
  onComplete: () => void;
  onClose: () => void;
}

// Extended state that tracks which appointment it was created for
interface InternalWizardState extends WizardState {
  appointmentId: number | null;
}

// Initial state factory that accepts steps and appointment ID
const createInitialState = (steps: StepConfig[], appointmentId: number | null): InternalWizardState => ({
  activeStep: 0,
  steps,
  loading: false,
  error: null,
  triageData: INITIAL_TRIAGE_DATA,
  confirmed: false,
  checkInPerformed: false,
  assignedQueueNumber: null,
  appointmentId,
});

/**
 * Hook for managing Quick Attendance Wizard state and actions
 */
export function useQuickAttendance({
  appointment,
  checkIn,
  startAttendance,
  completeAttendance,
  onComplete,
  onClose,
}: UseQuickAttendanceOptions): UseQuickAttendanceReturn {
  // Calculate applicable steps based on appointment status
  const applicableSteps = useMemo<StepConfig[]>(() => {
    if (!appointment) return [];
    return getApplicableSteps(appointment.attendanceStatus);
  }, [appointment]);

  const [state, setState] = useState<InternalWizardState>(() =>
    createInitialState(applicableSteps, appointment?.id ?? null)
  );

  // Reset state when appointment changes - using state comparison pattern
  // This is a valid React 18+ pattern: setting state during render when props change
  const currentAppointmentId = appointment?.id ?? null;
  if (state.appointmentId !== currentAppointmentId) {
    setState(createInitialState(applicableSteps, currentAppointmentId));
  }

  // Current step info
  const currentStep = state.steps[state.activeStep];
  const currentStepKey = currentStep?.key || 'confirmation';
  const isFirstStep = state.activeStep === 0;
  const isLastStep = state.activeStep === state.steps.length - 1;

  // Check if this appointment needs check-in
  const needsCheckIn = useMemo(() => {
    if (!appointment) return false;
    return appointment.attendanceStatus === AttendanceStatus.NOT_ATTENDED;
  }, [appointment]);

  /**
   * Handle moving to next step
   */
  const handleNext = useCallback(async () => {
    if (!appointment) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Handle confirmation step - perform check-in if needed
      if (currentStepKey === 'confirmation' && needsCheckIn) {
        const success = await checkIn(appointment.id);
        if (!success) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: 'Falha ao realizar check-in. Tente novamente.',
          }));
          return;
        }
        setState((prev) => ({
          ...prev,
          checkInPerformed: true,
        }));
      }

      // Handle start step - save triage (if any), initiate and complete attendance
      if (currentStepKey === 'start') {
        // Save triage data if any was filled
        if (hasTriageData(state.triageData)) {
          try {
            await receptionService.saveTriage(appointment.id, {
              bloodPressureSystolic: state.triageData.bloodPressureSystolic,
              bloodPressureDiastolic: state.triageData.bloodPressureDiastolic,
              heartRate: state.triageData.heartRate,
              temperature: state.triageData.temperature,
              weight: state.triageData.weight,
              height: state.triageData.height,
              oxygenSaturation: state.triageData.oxygenSaturation,
              observations: state.triageData.observations ?? null,
            });
          } catch (triageError) {
            console.error('Failed to save triage data:', triageError);
            // Continue with attendance even if triage save fails
          }
        }

        // Start attendance (transitions to IN_PROGRESS)
        const startSuccess = await startAttendance(appointment.id);
        if (!startSuccess) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: 'Falha ao iniciar atendimento. Tente novamente.',
          }));
          return;
        }

        // Complete attendance with QUICK type (for quick attendance flow)
        if (completeAttendance) {
          const completeSuccess = await completeAttendance(appointment.id, AttendanceType.QUICK);
          if (!completeSuccess) {
            setState((prev) => ({
              ...prev,
              loading: false,
              error: 'Falha ao concluir atendimento rapido. Tente novamente.',
            }));
            return;
          }
        }

        // Wizard complete
        setState((prev) => ({ ...prev, loading: false }));
        onComplete();
        return;
      }

      // Move to next step
      setState((prev) => ({
        ...prev,
        activeStep: prev.activeStep + 1,
        loading: false,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: 'Erro inesperado. Tente novamente.',
      }));
    }
  }, [appointment, checkIn, completeAttendance, startAttendance, currentStepKey, needsCheckIn, onComplete, state.triageData]);

  /**
   * Handle going back to previous step
   */
  const handleBack = useCallback(() => {
    if (isFirstStep) {
      onClose();
      return;
    }
    setState((prev) => ({
      ...prev,
      activeStep: prev.activeStep - 1,
      error: null,
    }));
  }, [isFirstStep, onClose]);

  /**
   * Skip triage step
   */
  const handleSkipTriage = useCallback(() => {
    setState((prev) => ({
      ...prev,
      activeStep: prev.activeStep + 1,
      triageData: INITIAL_TRIAGE_DATA,
    }));
  }, []);

  /**
   * Set confirmation checkbox state
   */
  const setConfirmed = useCallback((confirmed: boolean) => {
    setState((prev) => ({ ...prev, confirmed }));
  }, []);

  /**
   * Update triage data
   */
  const setTriageData = useCallback((triageData: TriageData) => {
    setState((prev) => ({ ...prev, triageData }));
  }, []);

  /**
   * Reset wizard state
   */
  const reset = useCallback(() => {
    setState(createInitialState(applicableSteps, appointment?.id ?? null));
  }, [applicableSteps, appointment?.id]);

  return {
    state,
    isFirstStep,
    isLastStep,
    currentStepKey,
    handleNext,
    handleBack,
    handleSkipTriage,
    setConfirmed,
    setTriageData,
    reset,
  };
}

export default useQuickAttendance;
